import { useQuery } from '@tanstack/react-query';
import { format, formatDistanceToNow } from 'date-fns';
import { he } from 'date-fns/locale';

import { supabase } from '../../lib/supabase';
import { entityLabel, labelFor } from '../../lib/audit-labels';
import styles from './settings.module.css';
import auditStyles from './audit.module.css';

interface AuditRow {
  id: string;
  action: string;
  entity_type: string | null;
  entity_id: string | null;
  user_id: string | null;
  created_at: string;
}

interface ProfileLite {
  user_id: string;
  full_name: string;
}

const PAGE_SIZE = 50;

async function fetchAuditPage(): Promise<{ rows: AuditRow[]; profiles: Record<string, string> }> {
  const { data: rows, error } = await supabase
    .from('audit_log')
    .select('id, action, entity_type, entity_id, user_id, created_at')
    .order('created_at', { ascending: false })
    .limit(PAGE_SIZE);
  if (error) throw error;
  const list = rows;

  const userIds = Array.from(new Set(list.map((r) => r.user_id).filter((v): v is string => v !== null)));
  let profiles: Record<string, string> = {};
  if (userIds.length > 0) {
    const { data: profs } = await supabase
      .from('user_profiles')
      .select('user_id, full_name')
      .in('user_id', userIds);
    profiles = (profs ?? []).reduce<Record<string, string>>((acc, p: ProfileLite) => {
      acc[p.user_id] = p.full_name;
      return acc;
    }, {});
  }

  return { rows: list, profiles };
}

function formatTime(iso: string): { rel: string; abs: string } {
  const d = new Date(iso);
  return {
    rel: formatDistanceToNow(d, { locale: he, addSuffix: true }),
    abs: format(d, 'dd/MM/yyyy HH:mm', { locale: he }),
  };
}

export function AuditLogPage() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['audit-log', 'page', PAGE_SIZE],
    queryFn: fetchAuditPage,
    staleTime: 30 * 1000,
  });

  if (isLoading) {
    return (
      <>
        <div className={styles.pageHead}>
          <h1>Audit Log</h1>
          <p className={styles.pageSub}>כל פעולה במערכת. נשמר 365 יום.</p>
        </div>
        <p className={styles.notice}>טוען רשומות...</p>
      </>
    );
  }

  if (error) {
    return (
      <>
        <div className={styles.pageHead}>
          <h1>Audit Log</h1>
          <p className={styles.pageSub}>כל פעולה במערכת. נשמר 365 יום.</p>
        </div>
        <p className={styles.formError}>לא הצלחנו לטעון את היומן · נסה שוב</p>
      </>
    );
  }

  const rows = data?.rows ?? [];
  const profiles = data?.profiles ?? {};

  return (
    <>
      <div className={styles.pageHead}>
        <h1>Audit Log</h1>
        <p className={styles.pageSub}>כל פעולה במערכת. נשמר 365 יום.</p>
      </div>

      {rows.length === 0 ? (
        <div className={styles.card}>
          <p className={styles.notice}>אין פעולות עדיין. התחל לעדכן פרטים בהגדרות וההיסטוריה תופיע כאן.</p>
        </div>
      ) : (
        <div className={auditStyles.list}>
          {rows.map((row) => {
            const time = formatTime(row.created_at);
            const userName = row.user_id ? (profiles[row.user_id] ?? 'משתמש') : 'מערכת';
            const ent = entityLabel(row.entity_type);
            return (
              <div key={row.id} className={auditStyles.row}>
                <div className={auditStyles.action}>
                  <strong>{labelFor(row.action)}</strong>
                  {ent ? <span className={auditStyles.chip}>{ent}</span> : null}
                </div>
                <div className={auditStyles.meta}>
                  <span className={auditStyles.user}>{userName}</span>
                  <span className={auditStyles.dot} aria-hidden="true">·</span>
                  <span className={auditStyles.time} title={time.abs}>
                    <span className="num">{time.rel}</span>
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {rows.length === PAGE_SIZE ? (
        <p className={styles.notice}>מציג <span className="num">{String(PAGE_SIZE)}</span> אחרונות. ייצוא ייכלל ב-Phase 5.</p>
      ) : null}
    </>
  );
}
